import React, { useCallback, useEffect, useState } from 'react';
import './app.scss';
import { Redirect, Route, Router, Switch, useHistory, useLocation } from 'react-router-dom';
import PerfectScrollbar from 'react-perfect-scrollbar';
import Header from '../components/header/header';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import { useDispatch, useSelector } from 'react-redux';
import { loading as PatientLoading, loadingAdmission, processing as PatientProcessing } from '../store/reducers/patient';
import { loading as DocumentLoading } from '../store/reducers/documents';
import { loading as PatientAdditionalProcessing } from '../store/reducers/additionalData';
import makeStyles from '@mui/styles/makeStyles';
import { loadPref } from '../store/reducers/preferences';
import { canEnterAdminPage, hasPermission, loadingUser, loadUser, Permissions, setLoadingUser, user } from '../store/reducers/user';
import { loadExternalLinks } from '../store/reducers/externalLinks';
import { SessionTimer } from '../components/header/sessionTimer';
import { setIpadSetting } from '../store/reducers/workflow';
import _ from 'lodash';
import { loadDicts } from '../store/reducers/dicts';
import { AgyleNamedIconView } from '../components/icons/agyle';
import { isDesktop } from 'react-device-detect';
import { loadWardList, setMapHospitalList, setMultiHospitalMode } from '../store/reducers/ward';
import { isSpecialSmallDeviceDetect } from '../util/isSpecialSmallDeviceDetect';
import WardDashboard from '../pages/wardDashboard/wardDashboard';
import AgyleHeader from '../components/header/agyleHeader/agyleHeader';
import clsx from 'clsx';
import { loadExternalConfigs } from '../store/reducers/externalConfig';
import Page404 from '../pages/error/page404';
import ServiceTemporarilyUnavailable from '../components/other/serviceTemporarilyUnavailable';
import useAgyleSnackbar from '../hooks/useAgyleSnackbar';
import API from './api';
import { setSnackbar } from '../store/reducers/globals';
import { BMLoading } from '../store/reducers/bedManagement';
import { CENTRAL_PAGE_PATH, MODULE_NAMES, MODULE_PATHS } from '../pages/centralPage/constants';
import { getConfig } from '../util/configUtil';
import DashboardRoutes from './extraRoute/DashboardRoutes';
import MainLoadingScreen from '../components/loading/mainLoadingScreen';
import PasConnectionChecker from '../components/modal/pasConnectionChecker';
import useAgyleRedirect from '../hooks/useAgyleRedirect';
import { SandboxDev } from '../components/sandbox/sandbox';
import { handleCis2Token, setCis2Authentication } from '../store/reducers/cis2';

const useStyles = makeStyles(() => ({
    backdrop: {
        zIndex: 9999, //theme.zIndex.drawer + 1,
    },
}));

// Lazy loading pages
const PatientContext = React.lazy(() => import('../pages/patient/patientContext/patientContext'));
const Maintenance = React.lazy(() => import('../pages/utils/maintenance'));
const Patients = React.lazy(() => import('../pages/patients/patients'));
const PatientSearch = React.lazy(() => import('../pages/patients/patientSearch'));
const Patient = React.lazy(() => import('../pages/patient/patient'));
const Audits = React.lazy(() => import('../pages/audit/audit'));
const CompleteAudits = React.lazy(() => import('../pages/audit/completeAudits'));
const WardView = React.lazy(() => import('../pages/ward/map/wardView'));
const Viewer = React.lazy(() => import('../pages/viewer/viewer'));
const HoldingPen = React.lazy(() => import('../pages/holding/holdingPen'));
const ResearchHoldingPen = React.lazy(() => import('../pages/holding/researchHoldingPen'));
const Research = React.lazy(() => import('../pages/research/research'));
const WardMapCreator = React.lazy(() => import('../pages/wardCreator/wardMapCreator'));
const AdmitPatientExt = React.lazy(() => import('../pages/admit/hl7Admit/admitPatientExt'));
const AdmitPatientExtRefresh = React.lazy(() => import('../pages/admit/admitExtRefresh/admitPatientExt'));
const AdmitPatient = React.lazy(() => import('../pages/admit/admitPatient'));
const Admin = React.lazy(() => import('../pages/admin/admin'));
const WorkList = React.lazy(() => import('../pages/bookAdmission/workList'));
const BookedAdmissions = React.lazy(() => import('../pages/bookedAdmissions/bookedAdmissions'));
const BookedAdmissionsDashboard = React.lazy(() => import('../pages/bookedAdmissions/dashboard/dashboard'));
const BookAdmissionLanding = React.lazy(() => import('../pages/bookAdmission/bookAdmissionLanding'));
const BookAdmissionForm = React.lazy(() => import('../pages/bookAdmission/createBookAdmissionForm'));
const Tutorials = React.lazy(() => import('../pages/tutorial/tutorials'));
const BedBookingTable = React.lazy(() => import('../pages/bedBooking/bedBookingTable'));
const BedManagement = React.lazy(() => import('../pages/bedManagement/bedManagementContext'));
const ReferralsListFlow = React.lazy(() => import('../pages/referralFlow/referralsListFlow'));
const CentralPage = React.lazy(() => import('../pages/centralPage/centralPage'));
const CleaningRequest = React.lazy(() => import('../pages/reports/cleaningRequestTable'));
const FHIRDashboard = React.lazy(() => import('../pages/admin/fhirsendadmin'));
const ActionPanel = React.lazy(() => import('../components/actionPanel/actionPanel'));
const AppointmentsRoot = React.lazy(() => import('../pages/appointments/appointmentsRoot'));
const MedicationsModule = React.lazy(() => import('../pages/medications/medicationsModule'));

// Header disable
const BUILDS_EXCLUDED = ['agyle', 'main'];
const PATHS_EXCLUDED = [
    { path: '/', option: 'equal' },
    { path: '/registry', option: 'equal' },
    { path: '/eadmit', option: 'equal' },
    { path: '/eadmitRefresh', option: 'equal' },
    { path: '/report/', option: 'startsWith' },
    { path: '/ward', option: 'startsWith' },
    { path: '/admin', option: 'startsWith' },
    { path: '/icons', option: 'startsWith' },
    { path: '/sandbox', option: 'startsWith' },
    { path: '/bookAdmission', option: 'startsWith' },
    { path: '/createBookAdmission', option: 'startsWith' },
    { path: '/bookedAdmissions', option: 'startsWith' },
    { path: '/bookAdmission', option: 'startsWith' },
    { path: '/training', option: 'equal' },
    { path: '/bedBooking', option: 'equal' },
    { path: '/fhirDashboard', option: 'startsWith' },
    { path: '/patient/', option: 'startsWith' },
    { path: '/patient-context/', option: 'startsWith' },
    { path: '/bedManagement', option: 'startsWith' },
    { path: CENTRAL_PAGE_PATH, option: 'startsWith' },
    { path: '/patient/attendance/', option: 'startsWith' },
    { path: CENTRAL_PAGE_PATH, option: 'startsWith' },
    { path: MODULE_PATHS[MODULE_NAMES.SCHEDULER], option: 'startsWith' },
    { path: MODULE_PATHS[MODULE_NAMES.MEDICATIONS_MODULE], option: 'startsWith' },
];

function App(props) {
    const history = useHistory();
    const classes = useStyles();
    const match = useLocation();
    const dispatch = useDispatch();

    // Snackbar listener
    useAgyleSnackbar();

    // Custom redirect path hook
    const redirect = useAgyleRedirect();

    // Config
    const centralPageEnabled = getConfig('modules.centralPage.enabled', false);
    const bedManagementEnabled = getConfig('modules.bedManagement.enabled', false);
    const adminModuleEnabled = getConfig('adminModuleEnabled', false);
    const adminModuleEnabledExceptionUsers = getConfig('adminModuleEnabledExceptionUsers', []);
    const registrationEnabled = getConfig('admitPatient.registration', false);
    const externalManagmentEnabled = getConfig('externalManagment.enabled', false);
    const cleaningRequestEnabled = getConfig('modules.cleaningRequest.enabled', false);
    const bookAdmissionModuleEnabled = getConfig('bookAdmissionModuleEnabled', false);
    const pasCheckConnection = getConfig('modules.pasHealthCheck.enabled', false);
    const externalPrescription = getConfig('modules.forms.prescription.externalVersion', false);

    // Selectors
    const userObject = useSelector(user);
    const loadingPatientObject = useSelector(PatientLoading);
    const loadingDocumentObject = useSelector(DocumentLoading);
    const processingPatientObject = useSelector(PatientProcessing);
    const processingPatientAdditionalObject = useSelector(PatientAdditionalProcessing);
    const bedManagementLoading = useSelector(BMLoading);
    const loadingAdmissionObject = useSelector(loadingAdmission);
    const userDataLoading = useSelector(loadingUser);

    const canBooking = dispatch(hasPermission(Permissions.AGYLE_CAN_VIEW_BED_BOOKING));
    const [openFromLink, setOpenFromLink] = useState(false);

    const [isPatientClosed, setIsPatientClosed] = useState(false);
    useEffect(() => {
        if (openFromLink && !isPatientClosed) {
            const ec = new BroadcastChannel('eyonix_channel');
            ec.onmessage = (event) => {
                console.log(event);
                setIsPatientClosed(true);
                window.open('/logout.html', '_self');
            };
            ec.postMessage('opening');

            return function cleanup() {
                ec.close();
            };
        }
    }, [isPatientClosed, openFromLink]);

    const setNewToken = (data) => {
        if (data.token) window.auth.setToken(data.token);
    };

    const handleWindowMessage = (e) => {
        let messageType = e.data.type;

        switch (messageType) {
            case 'new-token':
                setNewToken(e.data);
                break;
            default:
                break;
        }
    };

    useEffect(() => {
        dispatch(loadDicts);
        const queryParams = new URLSearchParams(window.location.search);
        if (queryParams.has('token')) {
            queryParams.delete('token');
            history.replace({
                search: queryParams.toString(),
            });
        }

        if (process.env.BUILD_MODULE === 'agyle') {
            // Checking whether to enable multi-hospital mode
            API.getHospitals('forMCT eq true')
                .then((data) => {
                    dispatch(setMapHospitalList(data?.data?.__data));
                    dispatch(setMultiHospitalMode(data?.data?.__data?.length > 1));
                })
                .catch((err) => {
                    dispatch(
                        setSnackbar({
                            type: 'error',
                            message: 'There was a problem retrieving the list of hospitals.',
                        }),
                    );
                    console.error(err);
                });

            if (queryParams.has('cis2data') || queryParams.has('response')) {
                // TODO: sandbox test data
                // let data = {
                //     message: 'Authenticated',
                //     user: {
                //         nhsid_useruid: '555371018105',
                //         name: 'Mielnik Krzysztof',
                //         nhsid_nrbac_roles: [
                //             {
                //                 person_orgid: '555371019103',
                //                 person_roleid: '555371020101',
                //                 org_code: 'A9A5A',
                //                 role_name: '"Support":"Systems Support":"Systems Support Access Role"',
                //                 role_code: 'S8001:G8005:R8015',
                //             },
                //             {
                //                 person_orgid: '555378592102',
                //                 person_roleid: '555378593100',
                //                 org_code: 'F81640',
                //                 role_name: '"M&D":"Medical - M&D":"General Medical Practitioner"',
                //                 role_code: 'S0010:G0020:R0260',
                //                 activities: [
                //                     'Manage Outbound Referrals',
                //                     'Proxy Manage Outbound Referrals',
                //                     'Manage Outbound Appointments',
                //                 ],
                //                 activity_codes: ['B1101', 'B1102', 'B1103'],
                //             },
                //             {
                //                 person_orgid: '555378596103',
                //                 person_roleid: '555378597101',
                //                 org_code: 'J2X6U',
                //                 role_name: '"M&D":"Medical - M&D":"Consultant"',
                //                 role_code: 'S0010:G0020:R0050',
                //                 activities: [
                //                     'Maintain Service Configurations and Workgroups',
                //                     'Manage Inbound Referrals',
                //                     'Proxy Manage Inbound Referrals',
                //                     'Manage Inbound Appointments',
                //                 ],
                //                 activity_codes: ['B1107', 'B1110', 'B1115', 'B1120'],
                //             },
                //         ],
                //         given_name: 'Krzysztof',
                //         family_name: 'Mielnik',
                //         uid: '555371018105',
                //         sub: '555371018105',
                //     },
                //     token_summary: {
                //         access_token: 'JPDp7JKjfxGUXq1gnGZj3nwi1zYw',
                //         scope: null,
                //         expires_in: '599',
                //         token_type: 'Bearer',
                //     },
                // };
                //
                // const cis2dataEncoded = btoa(JSON.stringify(data));

                const cis2dataEncoded = queryParams.get('cis2data') || queryParams.get('response');

                try {
                    const cis2data = JSON.parse(atob(cis2dataEncoded));
                    dispatch(handleCis2Token(cis2data));
                } catch (error) {
                    console.error('Invalid cis2data payload', error);
                    dispatch(handleCis2Token(null));
                }

                queryParams.delete('cis2data');
                queryParams.delete('response');
                history.replace({
                    search: queryParams.toString(),
                });
            }
        }

        window.addEventListener('message', handleWindowMessage);

        return () => {
            window.removeEventListener('message', handleWindowMessage);
        };
    }, []);

    if (new URL(document.location).searchParams.get('touch') === 'false') {
        dispatch(setIpadSetting({ backend: false }));
    }

    const offGesture = () => {
        document.addEventListener('gesturestart', function (e) {
            e.preventDefault();
            document.body.style.zoom = 1;
        });

        document.addEventListener('gesturechange', function (e) {
            e.preventDefault();
            document.body.style.zoom = 1;
        });

        document.addEventListener('gestureend', function (e) {
            e.preventDefault();
            document.body.style.zoom = 1;
        });
    };

    useEffect(() => {
        dispatch(setLoadingUser(true));
        dispatch(loadUser());
        dispatch(loadPref());
        if (process.env.BUILD_MODULE === 'agyle') {
            dispatch(loadWardList());
            dispatch(loadExternalConfigs());
        }
        if (!_.get(window, 'envConfig.externalSystemLinks.disabled', false)) {
            dispatch(loadExternalLinks());
        }
        offGesture();

        if (match.search.includes('context')) {
            setOpenFromLink(true);
        }

        document.addEventListener('touchmove', preventDefaultEvent);
        return () => {
            document.removeEventListener('touchmove', preventDefaultEvent);
        };
    }, []);

    useEffect(() => {
        document.addEventListener('keydown', handleKeyPress);

        return () => {
            document.removeEventListener('keydown', handleKeyPress);
        };
    }, [handleKeyPress]);

    const preventDefaultEvent = (e) => {
        // Disable mobile page refresh
        e.preventDefault();
    };

    function handleKeyPress(event) {
        if (event.code === 'KeyL' && event.ctrlKey) {
            event.preventDefault();
            window.auth.logout();
        }
    }

    const isSmallDevice = isSpecialSmallDeviceDetect();

    const getBodyHeight = () => {
        if (
            process.env.BUILD_MODULE === 'agyle' &&
            match &&
            match.pathname &&
            (match.pathname.startsWith('/admin') ||
                match.pathname.startsWith('/bookedAdmissions') ||
                match.pathname.startsWith('/bedManagement') ||
                match.pathname.startsWith('/report') ||
                match.pathname === '/wards' ||
                match.pathname === '/wardNew' ||
                match.pathname.startsWith('/sandbox') ||
                match.pathname === CENTRAL_PAGE_PATH ||
                match.pathname.startsWith(MODULE_PATHS[MODULE_NAMES.SCHEDULER]))
        ) {
            return '100vh';
        }

        if (process.env.BUILD_MODULE === 'agyle' && match && match.pathname && match.pathname.startsWith('/ward')) {
            return 'initial';
        }
        if (isSmallDevice) return 'calc(100vh - 100px)';

        if (match.pathname === '/admin') {
            return '100vh';
        }
        let header = process.env.BUILD_MODULE === 'agyle' ? 70 : 53;
        return `calc(100vh - ${header}px)`;
    };

    const displayHeader = useCallback(() => {
        const builds = BUILDS_EXCLUDED.includes(process.env.BUILD_MODULE);
        let path = false;

        if (match && match.pathname) {
            _.forEach(PATHS_EXCLUDED, (opt) => {
                if (path) return;

                switch (opt.option) {
                    case 'startsWith':
                        path = match.pathname.startsWith(opt.path);
                        break;
                    case 'equal':
                    default:
                        path = match.pathname === opt.path;
                        break;
                }
            });
        }

        return !(builds && path);
    }, [match]);

    const handleCheckPanel = () => {
        return !!match?.pathname?.startsWith('/search');
    };

    const renderSystemHeader = () => {
        if (process.env.BUILD_MODULE === 'agyle') {
            if (displayHeader()) return <AgyleHeader displayLink showActionPanel={handleCheckPanel()} />;
        } else {
            if (match.pathname !== '/') return <Header openFromLink={openFromLink} />;
        }
        return null;
    };

    if (sessionStorage.getItem('maintenance')) {
        return (
            <Switch>
                <Route path="/">
                    <Maintenance />
                </Route>
            </Switch>
        );
    }

    if (userDataLoading) {
        return (
            <Backdrop className={classes.backdrop} open={true}>
                <CircularProgress color="inherit" size={64} />
            </Backdrop>
        );
    }

    return (
        <div id="app">
            <Backdrop
                className={classes.backdrop}
                open={
                    !!loadingPatientObject ||
                    !!loadingDocumentObject ||
                    !!processingPatientObject ||
                    !!processingPatientAdditionalObject ||
                    !!bedManagementLoading ||
                    !!loadingAdmissionObject
                }
            >
                <CircularProgress color="inherit" size={64} />
            </Backdrop>

            {!props.isFrame && <SessionTimer />}

            {renderSystemHeader()}

            {process.env.BUILD_MODULE === 'agyle' && (
                <>
                    <ServiceTemporarilyUnavailable />
                    <ActionPanel />
                    {pasCheckConnection && <PasConnectionChecker />}
                </>
            )}

            <div
                className={clsx(process.env.BUILD_MODULE === 'main' && 'eyonix', process.env.BUILD_MODULE === 'agyle' && 'agyle')}
                style={{ height: getBodyHeight() }}
            >
                <PerfectScrollbar
                    ref={(ref) => {
                        window._scrollBarRef = ref;
                    }}
                >
                    <React.Suspense fallback={<MainLoadingScreen />}>
                        <Router history={history}>
                            <Switch>
                                <Route path="/ward/:mapId">
                                    <WardView />
                                </Route>

                                <Route path="/wards">
                                    <WardDashboard />
                                </Route>

                                {centralPageEnabled && (
                                    <Route path={CENTRAL_PAGE_PATH}>
                                        <CentralPage />
                                    </Route>
                                )}

                                <Route path="/patient/attendance/:attId">
                                    <PatientContext standaloneMode />
                                </Route>

                                <Route path="/patient-context/:ptId">
                                    <PatientContext standaloneMode loadOnlyPatient />
                                </Route>

                                <Route path="/patient">
                                    <Patient />
                                </Route>

                                <Route path="/view/:patientId/:documentIds">
                                    <Viewer />
                                </Route>

                                <Route path="/holding">
                                    <HoldingPen />
                                </Route>
                                <Route path="/researchholding">
                                    <ResearchHoldingPen />
                                </Route>
                                <Route exact path="/research">
                                    <Research />
                                </Route>

                                {process.env.BUILD_MODULE !== 'agyle' && (
                                    <Route path="/search">
                                        <Patients />
                                    </Route>
                                )}

                                {process.env.BUILD_MODULE === 'agyle' && (
                                    <Route path="/search">
                                        <PatientSearch />
                                    </Route>
                                )}

                                <Route path="/wardCreator/:id">
                                    <WardMapCreator />
                                </Route>

                                <Route path="/audits">
                                    <Audits />
                                </Route>

                                <Route path="/completeAudits">
                                    <CompleteAudits />
                                </Route>

                                {bedManagementEnabled && (
                                    <Route path="/bedManagement">
                                        <BedManagement />
                                    </Route>
                                )}
                                <Route path="/referral">
                                    <ReferralsListFlow flow />
                                </Route>

                                <Route path="/admWorkList">
                                    <WorkList />
                                </Route>

                                <Route path="/fhirDashboard">
                                    <FHIRDashboard fromRoute />
                                </Route>

                                {(adminModuleEnabled || adminModuleEnabledExceptionUsers.includes(userObject.userName)) &&
                                    isDesktop &&
                                    dispatch(canEnterAdminPage()) && (
                                        <Route path="/admin">
                                            <Admin />
                                        </Route>
                                    )}

                                {externalManagmentEnabled && (
                                    <Route path="/eadmit">
                                        <AdmitPatientExt disableHeader />
                                    </Route>
                                )}

                                {externalManagmentEnabled && (
                                    <Route path="/eadmitRefresh">
                                        <AdmitPatientExtRefresh />
                                    </Route>
                                )}

                                {registrationEnabled && (
                                    <Route path={MODULE_PATHS[MODULE_NAMES.PATIENT_REGISTRY]}>
                                        <AdmitPatient />
                                    </Route>
                                )}

                                {cleaningRequestEnabled && (
                                    <Route path="/report/cleaningRequest">
                                        <CleaningRequest />
                                    </Route>
                                )}

                                {/*that is a helper to find icons names*/}
                                {process.env.NODE_ENV === 'development' && (
                                    <Route path="/icons">
                                        <AgyleNamedIconView />
                                    </Route>
                                )}
                                {/*//temporary for show viewer to Nick*/}
                                {(process.env.NODE_ENV === 'development' || true) && (
                                    <Route path="/sandbox">
                                        <SandboxDev />
                                    </Route>
                                )}

                                {bookAdmissionModuleEnabled && isDesktop && (
                                    <Route path="/bookAdmission">
                                        <BookAdmissionLanding />
                                    </Route>
                                )}

                                {bookAdmissionModuleEnabled && isDesktop && (
                                    <Route path="/createBookAdmission/:patientId/:operationId">
                                        <BookAdmissionForm />
                                    </Route>
                                )}

                                {bookAdmissionModuleEnabled && isDesktop && (
                                    <Route path="/bookedAdmissions">
                                        <BookedAdmissions isFrame={props.isFrame} />
                                    </Route>
                                )}

                                {bookAdmissionModuleEnabled && isDesktop && (
                                    <Route path="/bookedAdmissionsDashboard">
                                        <BookedAdmissionsDashboard isFrame={props.isFrame} />
                                    </Route>
                                )}

                                {isDesktop && (
                                    <Route path={MODULE_PATHS[MODULE_NAMES.SCHEDULER]}>
                                        <AppointmentsRoot isStandalone={false} />
                                    </Route>
                                )}

                                <Route path="/training">
                                    <Tutorials />
                                </Route>

                                {externalPrescription && (
                                    <Route path="/medications">
                                        <MedicationsModule />
                                    </Route>
                                )}

                                {canBooking && (
                                    <Route path="/bedBooking">
                                        <BedBookingTable />
                                    </Route>
                                )}

                                <Route path="/insight" component={DashboardRoutes} />
                                {/*<Route path="/sandbox" component={SandboxDev} />*/}
                                <Route exact path="/">
                                    {process.env.BUILD_MODULE === 'agyle' && <Redirect to={redirect} />}
                                    {process.env.BUILD_MODULE !== 'agyle' &&
                                        process.env.BUILD_MODULE !== 'lwh' &&
                                        process.env.BUILD_MODULE !== 'stg' &&
                                        process.env.BUILD_MODULE !== 'dch' &&
                                        process.env.BUILD_MODULE !== 'mse' && <Patients />}
                                    {(process.env.BUILD_MODULE === 'lwh' ||
                                        process.env.BUILD_MODULE === 'stg' ||
                                        process.env.BUILD_MODULE === 'dch' ||
                                        process.env.BUILD_MODULE === 'mse') && <HoldingPen />}
                                </Route>
                                <Route component={Page404} />
                            </Switch>
                        </Router>
                    </React.Suspense>
                </PerfectScrollbar>
            </div>
        </div>
    );
}

export default App;
